import { useState, useEffect } from 'react'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@retailsync/ui'
import { connectorApi } from '../services/api/connector'
import { ConnectorTemplate, ConnectorApiError } from '../services/api/connector'

export function Settings() {
  const [templates, setTemplates] = useState<ConnectorTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ConnectorTemplate | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    posSystem: '',
    fileType: 'csv' as 'csv' | 'excel',
    delimiter: ',',
    hasHeaders: true,
    fieldMappings: [] as any[]
  })

  useEffect(() => {
    fetchTemplates()
  }, [])

  const fetchTemplates = async () => {
    try {
      setLoading(true)
      const data = await connectorApi.getTemplates()
      setTemplates(data)
    } catch (err) {
      console.error('Failed to fetch templates:', err)
      setError('Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateTemplate = async () => {
    try {
      const response = await connectorApi.createTemplate(formData)
      if (response.success) {
        setTemplates([...templates, response.data])
        setShowCreateForm(false)
        resetForm()
      } else {
        setError(response.error || 'Failed to create template')
      }
    } catch (err) {
      console.error('Failed to create template:', err)
      setError(err instanceof ConnectorApiError ? err.message : 'Failed to create template')
    }
  }

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return

    try {
      const response = await connectorApi.updateTemplate(editingTemplate.id, formData)
      if (response.success) {
        setTemplates(templates.map(t => t.id === editingTemplate.id ? response.data : t))
        setEditingTemplate(null)
        resetForm()
      } else {
        setError(response.error || 'Failed to update template')
      }
    } catch (err) {
      console.error('Failed to update template:', err)
      setError(err instanceof ConnectorApiError ? err.message : 'Failed to update template')
    }
  }

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Are you sure you want to delete this template?')) return

    try {
      const response = await connectorApi.deleteTemplate(templateId)
      if (response.success) {
        setTemplates(templates.filter(t => t.id !== templateId))
      } else {
        setError(response.error || 'Failed to delete template')
      }
    } catch (err) {
      console.error('Failed to delete template:', err)
      setError(err instanceof ConnectorApiError ? err.message : 'Failed to delete template')
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      posSystem: '',
      fileType: 'csv',
      delimiter: ',',
      hasHeaders: true,
      fieldMappings: []
    })
  }

  const startEdit = (template: ConnectorTemplate) => {
    setEditingTemplate(template)
    setFormData({
      name: template.name,
      description: template.description,
      posSystem: template.posSystem,
      fileType: template.fileType,
      delimiter: template.delimiter || ',',
      hasHeaders: template.hasHeaders,
      fieldMappings: template.fieldMappings
    })
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Template Management</h1>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Template Management</h1>
          <p className="mt-1 text-sm text-gray-600">Create and manage data import templates</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="self-start sm:self-auto">
          Create Template
        </Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3">
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 text-sm text-red-600 hover:text-red-800"
            >
              Dismiss
            </button>
          </CardContent>
        </Card>
      )}

      {(showCreateForm || editingTemplate) && (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <h3 className="text-lg font-medium mb-4">
              {editingTemplate ? 'Edit Template' : 'Create New Template'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tpl-name">Name</Label>
                <Input
                  id="tpl-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Square Products"
                />
              </div>
              <div>
                <Label htmlFor="tpl-pos">POS System</Label>
                <Input
                  id="tpl-pos"
                  value={formData.posSystem}
                  onChange={(e) => setFormData({ ...formData, posSystem: e.target.value })}
                  placeholder="e.g., square, shopify, moniepoint"
                />
              </div>
              <div>
                <Label htmlFor="tpl-type">File Type</Label>
                <Select
                  value={formData.fileType}
                  onValueChange={(v) =>
                    setFormData({ ...formData, fileType: v as 'csv' | 'excel' })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="excel">Excel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.fileType === 'csv' && (
                <div>
                  <Label htmlFor="tpl-delimiter">Delimiter</Label>
                  <Input
                    id="tpl-delimiter"
                    value={formData.delimiter}
                    onChange={(e) => setFormData({ ...formData, delimiter: e.target.value })}
                    placeholder=","
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <Label htmlFor="tpl-desc">Description</Label>
                <Textarea
                  id="tpl-desc"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Describe this template..."
                />
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateForm(false)
                  setEditingTemplate(null)
                  resetForm()
                }}
              >
                Cancel
              </Button>
              <Button onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}>
                {editingTemplate ? 'Update' : 'Create'} Template
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Template</TableHead>
            <TableHead>System</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {templates.map((template) => (
            <TableRow key={template.id}>
              <TableCell>
                <div className="font-medium text-gray-900">{template.name}</div>
                <div className="text-xs text-gray-500">{template.description}</div>
              </TableCell>
              <TableCell className="whitespace-nowrap text-gray-900">
                {template.posSystem}
              </TableCell>
              <TableCell>
                <Badge variant="info">{template.fileType.toUpperCase()}</Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => startEdit(template)}
                    className="text-brand-600 hover:text-brand-800 text-sm font-medium"
                  >
                    Edit
                  </button>
                  {!template.isSystemTemplate && (
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}